/**
 * GRIDHEIMR -- Bot Steps Component
 *
 * Renders a real-time timeline of agent step events received via SSE.
 * Steps and step_results are merged by step_number to show a unified view:
 * action + target on the left, reasoning in the middle, outcome badge on the right.
 */

import React, { useEffect, useRef, useMemo } from "react";
import type { AgentStep } from "../hooks/useAgentSteps";
import { BLUE, YELLOW, GREEN, RED, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface BotStepsProps {
  steps: AgentStep[];
  connected: boolean;
}

/**
 * Merged step row combining step + step_result by step_number
 */
interface MergedStep {
  step_number: number;
  action?: string;
  target?: string;
  reasoning?: string;
  outcome?: string;
  duration_ms?: number;
  error_message?: string;
  ts: string;
}

/**
 * Merge step and step_result events by step_number
 */
function mergeSteps(steps: AgentStep[]): MergedStep[] {
  const map = new Map<number, MergedStep>();

  for (const step of steps) {
    const existing = map.get(step.step_number);

    if (existing) {
      // Merge fields from the new event into the existing entry
      if (step.action !== undefined) existing.action = step.action;
      if (step.target !== undefined) existing.target = step.target;
      if (step.reasoning !== undefined) existing.reasoning = step.reasoning;
      if (step.outcome !== undefined) existing.outcome = step.outcome;
      if (step.duration_ms !== undefined) existing.duration_ms = step.duration_ms;
      if (step.error_message !== undefined) existing.error_message = step.error_message;
      // Update timestamp to latest
      existing.ts = step.ts;
    } else {
      map.set(step.step_number, {
        step_number: step.step_number,
        action: step.action,
        target: step.target,
        reasoning: step.reasoning,
        outcome: step.outcome,
        duration_ms: step.duration_ms,
        error_message: step.error_message,
        ts: step.ts,
      });
    }
  }

  // Return sorted by step_number
  return Array.from(map.values()).sort((a, b) => a.step_number - b.step_number);
}

/**
 * Get outcome badge color
 */
function getOutcomeColor(outcome?: string): string {
  switch (outcome) {
    case "success":
      return GREEN;
    case "failure":
      return YELLOW;
    case "error":
      return RED;
    default:
      return "rgba(255,255,255,0.3)";
  }
}

/**
 * Format duration in milliseconds to human-readable string
 */
function formatDuration(ms?: number): string {
  if (ms === undefined) return "";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Bot Steps Component
 *
 * Renders the real-time agent step timeline with connection indicator,
 * step actions, reasoning, and outcome badges.
 */
export function BotSteps({ steps, connected }: BotStepsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Merge step + step_result events
  const merged = useMemo(() => mergeSteps(steps), [steps]);

  // Auto-scroll to bottom when new steps arrive
  useEffect(() => {
    if (containerRef.current) {
      const el = containerRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [merged.length]);

  return (
    <div style={styles.wrapper}>
      {/* Section Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>AGENT STEPS</h3>
        <div style={styles.connectionIndicator}>
          <span
            style={{
              ...styles.connectionDot,
              backgroundColor: connected ? GREEN : "rgba(255,255,255,0.3)",
              boxShadow: connected ? `0 0 6px ${GREEN}` : "none",
            }}
          />
          <span style={{ ...styles.connectionText, color: connected ? GREEN : "rgba(255,255,255,0.3)" }}>
            {connected ? "LIVE" : "OFFLINE"}
          </span>
        </div>
      </div>

      {/* Steps Container */}
      <div ref={containerRef} style={styles.container}>
        {merged.length === 0 && connected && (
          <p style={styles.emptyMessage}>Waiting for agent steps...</p>
        )}
        {merged.length === 0 && !connected && (
          <p style={styles.emptyMessage}>Agent not streaming</p>
        )}

        {merged.map((step) => (
          <div key={step.step_number} style={styles.stepRow}>
            {/* Step Number */}
            <div style={styles.stepNumber}>
              <span style={styles.stepNumberText}>#{step.step_number}</span>
            </div>

            {/* Step Content */}
            <div style={styles.stepContent}>
              {/* Action + Target */}
              <div style={styles.actionLine}>
                {step.action && (
                  <span style={styles.actionText}>{step.action}</span>
                )}
                {step.target && (
                  <span style={styles.targetText}>{step.target}</span>
                )}
              </div>

              {/* Reasoning */}
              {step.reasoning && (
                <p style={styles.reasoningText}>{step.reasoning}</p>
              )}

              {/* Error Message */}
              {step.error_message && (
                <p style={styles.errorText}>{step.error_message}</p>
              )}
            </div>

            {/* Outcome Badge + Duration */}
            <div style={styles.stepMeta}>
              {step.outcome && (
                <span
                  style={{
                    ...styles.outcomeBadge,
                    borderColor: getOutcomeColor(step.outcome),
                    color: getOutcomeColor(step.outcome),
                  }}
                >
                  {step.outcome.toUpperCase()}
                </span>
              )}
              {step.duration_ms !== undefined && (
                <span style={styles.durationText}>
                  {formatDuration(step.duration_ms)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Inline styles (GRIDHEIMR theme)
 */
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  title: {
    margin: 0,
    fontFamily: PIXEL,
    fontSize: "0.5rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.1em",
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  connectionIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
  },
  connectionDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    transition: "all 0.3s ease",
  },
  connectionText: {
    fontFamily: PIXEL,
    fontSize: "0.35rem",
    letterSpacing: "0.1em",
    transition: "color 0.3s ease",
  },
  container: {
    maxHeight: "400px",
    overflowY: "auto" as const,
    border: `1px solid rgba(0,106,167,0.15)`,
    background: "rgba(0,10,18,0.6)",
  },
  emptyMessage: {
    textAlign: "center" as const,
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.3)",
    padding: "2rem 1rem",
    margin: 0,
  },
  stepRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75rem",
    padding: "0.75rem 1rem",
    borderBottom: `1px solid rgba(0,106,167,0.08)`,
  },
  stepNumber: {
    flexShrink: 0,
    width: "36px",
    textAlign: "right" as const,
  },
  stepNumberText: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: BLUE,
    fontWeight: 700,
  },
  stepContent: {
    flex: 1,
    minWidth: 0,
  },
  actionLine: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flexWrap: "wrap" as const,
  },
  actionText: {
    fontFamily: PIXEL,
    fontSize: "0.35rem",
    color: "#fff",
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
  targetText: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.6)",
  },
  reasoningText: {
    margin: "0.3rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.35)",
    lineHeight: 1.4,
  },
  errorText: {
    margin: "0.3rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: RED,
    lineHeight: 1.4,
  },
  stepMeta: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-end",
    gap: "0.25rem",
  },
  outcomeBadge: {
    fontFamily: PIXEL,
    fontSize: "0.3rem",
    letterSpacing: "0.08em",
    padding: "3px 6px",
    border: "1px solid",
  },
  durationText: {
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.3)",
  },
};
